import { useCallback, useMemo, useState } from 'react';
import {
  calculateTotals,
  getFormSubTitle,
  getFormTitle,
  handleUpdateRowFields,
  transformFormData
} from '../../../utils';
import { ReceiptVoucherFormData, receiptVoucherInitialValues, receiptVoucherSchema } from '../../../schema';
import { useDetailRows, useFormFieldState } from '../../../hooks';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import type { PhieuThuDetailRow } from '../../../types';
import { useAuth } from '@/contexts/auth-context';
import type { PhieuThuFormProps } from '../types';

export const useFormDialog = ({
  formMode,
  initialData,
  onSubmit,
  onClose,
  currentIndex,
  totalCount
}: PhieuThuFormProps) => {
  const [editExchangeRate, setEditExchangeRate] = useState(false);
  const { entityUnit } = useAuth();
  const formState = useFormFieldState(initialData);
  const { state } = formState;
  const detail = useDetailRows<PhieuThuDetailRow>(initialData?.child_data, undefined, handleUpdateRowFields);
  const repeatRows = useRepeatRowsHandler({
    setRows: detail.setRows
  });

  const totals = useMemo(() => calculateTotals(detail.rows), [detail.rows]);
  const initialValues = useMemo(
    () =>
      initialData
        ? {
            ...initialData,
            ty_gia: Number(initialData.ty_gia || receiptVoucherInitialValues.ty_gia)
          }
        : receiptVoucherInitialValues,
    [initialData]
  );

  const submit = useCallback(
    async (formData: ReceiptVoucherFormData) => {
      const data = transformFormData(formData, state, detail.rows, totals, entityUnit, formMode);
      await onSubmit?.({ ...data, unit_id: entityUnit?.uuid || '' });
    },
    [detail.rows, entityUnit, formMode, onSubmit, state, totals]
  );

  return {
    formMode,
    initialData,
    initialValues,
    title: getFormTitle(formMode),
    subTitle: getFormSubTitle(currentIndex, totalCount),
    schema: receiptVoucherSchema,
    formState,
    onClose,
    submit,
    totals,
    detail: {
      ...detail,
      repeatRows
    },
    exchangeRate: {
      edit: editExchangeRate,
      setEdit: setEditExchangeRate
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
